import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useCountriesQuery } from 'src/reducers/contactApiSlice';

import { BUSINESS_LANGUAGES } from '../../../../../common/constants';
import LanguageSelector from '../../../../../components/languageSelector/LanguageSelector';
import { selectActiveBusiness, selectBusinesses } from '../../../../../reducers/business/businessSlice';
import StoreLocationMap from './StoreLocationMap';

const StoreDetails = ({ control, register, watch, errors, setValue, trigger, defaultLocale }) => {
	const businesses = useSelector(selectBusinesses);
	const activeBusiness = useSelector(selectActiveBusiness);

	const { data: countries = [], isLoading: isLoadingCountries } = useCountriesQuery({});

	const translations = BUSINESS_LANGUAGES.reduce((acc, lang) => {
		acc[lang?.code] = {
			title: watch(`translations[${lang?.code}].title`),
			description: watch(`translations[${lang?.code}].description`),
		};
		return acc;
	}, {});

	const defaultLocaleIdx = BUSINESS_LANGUAGES.findIndex((language) => language?.code === defaultLocale);
	const [teamNotesLang, setTeamNotesLang] = useState(BUSINESS_LANGUAGES[defaultLocaleIdx ?? 0]?.code ?? 'el');
	const handleLanguageChange = (language: string) => setTeamNotesLang(language);

	// Google maps autocomplete
	const autoCompleteRef = useRef();
	const inputRef = useRef(null);
	const options = {
		componentRestrictions: { country: 'gr' },
		fields: ['address_components', 'geometry', 'name'],
		types: ['address'],
	};
	let address: {
		location: {
			street_address: string;
			street: string;
			street_no: string;
			city: string;
			area: string;
			post_code: string;
			country: string;
			lat?: number | string;
			lng?: number | string;
			coords: {
				type: string;
				coordinates: [any, any];
			};
			translations: {
				[key: string]: {
					area: string;
					city: string;
					country: string;
					street_address: string;
					street: string;
					street_no: string;
				};
			};
		};
	} = {
		location: {
			street_address: '',
			street: '',
			street_no: '',
			city: '',
			area: '',
			post_code: '',
			country: '',
			lat: '',
			lng: '',
			coords: {
				type: 'Point',
				coordinates: ['', ''],
			},
			translations: {
				el: {
					area: '',
					city: '',
					country: '',
					street_address: '',
					street: '',
					street_no: '',
				},
				en: {
					area: '',
					city: '',
					country: '',
					street_address: '',
					street: '',
					street_no: '',
				},
			},
		},
	};

	const intl = useIntl();

	type TranslationField = 'street' | 'street_no' | 'city' | 'area' | 'country';

	const applyAddressComponentsToLocationForm = (address_components: google.maps.GeocoderAddressComponent[], language: string) => {
		let streetName = '';
		let streetNumber = '';

		const setTranslationValue = (field: string, value: string) => {
			setValue(`location.translations.${language}.${field}`, value, { shouldDirty: true });
			if (language === defaultLocale) {
				setValue(`location.${field}`, value, { shouldDirty: true });
			}
		};

		address_components.forEach((component) => {
			const types = component.types;

			if (types.includes('street_number')) {
				streetNumber = component.long_name;
			}
			if (types.includes('route')) {
				streetName = component.long_name;
			}
			if (types.includes('locality')) {
				setTranslationValue('city', component.long_name);
			}
			if (types.includes('administrative_area_level_3') || types.includes('neighborhood') || types.includes('sublocality')) {
				setTranslationValue('area', component.long_name);
			}
			if (types.includes('postal_code')) {
				const postalCode = component.long_name;
				const currentPostCode = watch('location.post_code');
				if (!currentPostCode || language === defaultLocale) {
					setValue('location.post_code', postalCode, { shouldDirty: true });
				}
			}
			if (types.includes('country')) {
				setTranslationValue('country', component.short_name);
			}
		});

		const fullAddress = `${streetName} ${streetNumber}`.trim();
		setTranslationValue('street', streetName);
		setTranslationValue('street_no', streetNumber);
		if (!watch('location.street_address') || language === defaultLocale) {
			setValue('location.street_address', fullAddress, { shouldDirty: true });
		}

		if (language === 'en') {
			const fallbackFields: TranslationField[] = ['street', 'street_no', 'city', 'area', 'country'];
			fallbackFields.forEach((field) => {
				const currentValue = watch(`location.translations.en.${field}`);
				if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
					return;
				}

				const defaultTranslationValue = watch(`location.translations.${defaultLocale}.${field}`);
				const fallbackValue =
					(defaultTranslationValue !== undefined && defaultTranslationValue !== null
						? defaultTranslationValue
						: watch(`location.${field}`)) ?? '';
				if (fallbackValue !== '') {
					setValue(`location.translations.en.${field}`, fallbackValue, { shouldDirty: true });
				}
			});
		}
	};

	const getTranslationFieldValue = (language: string, field: TranslationField) => {
		const activeLanguage = language || defaultLocale || '';
		if (!activeLanguage) {
			const rootValueFallback = watch(`location.${field}`);
			return (rootValueFallback as string) ?? '';
		}

		const translationValue = watch(`location.translations.${activeLanguage}.${field}`);
		if (translationValue !== undefined && translationValue !== null) {
			if (activeLanguage === defaultLocale || translationValue !== '') {
				return translationValue;
			}
		}

		if (activeLanguage === defaultLocale) {
			return translationValue;
		}

		const defaultTranslationValue = defaultLocale !== undefined ? watch(`location.translations.${defaultLocale}.${field}`) : undefined;
		if (defaultTranslationValue !== undefined && defaultTranslationValue !== null && defaultTranslationValue !== '') {
			return defaultTranslationValue;
		}

		const rootValue = watch(`location.${field}`);
		if (rootValue !== undefined && rootValue !== null) {
			return rootValue;
		}

		return '';
	};

	const updateStreetAddressFromParts = (language: string, field: TranslationField, value: string) => {
		if (!language || language !== defaultLocale) {
			return;
		}

		if (field !== 'street' && field !== 'street_no') {
			return;
		}

		const street = field === 'street' ? value : watch(`location.translations.${language}.street`) ?? watch('location.street') ?? '';
		const streetNo = field === 'street_no' ? value : watch(`location.translations.${language}.street_no`) ?? watch('location.street_no') ?? '';

		const combined = [street, streetNo]
			.filter((part) => part && part.toString().trim().length > 0)
			.join(' ')
			.trim();
		setValue('location.street_address', combined, { shouldDirty: true });
	};

	const handleTranslationFieldChange = (language: string, field: TranslationField, value: string) => {
		const activeLanguage = language || defaultLocale;
		if (!activeLanguage) {
			return;
		}

		const nextValue = field === 'country' ? value.toUpperCase() : value;

		setValue(`location.translations.${activeLanguage}.${field}`, nextValue, { shouldDirty: true });

		if (activeLanguage === defaultLocale) {
			setValue(`location.${field}`, nextValue, { shouldDirty: true });
			updateStreetAddressFromParts(activeLanguage, field, nextValue);
		}
	};

	const latLngInputValue = (v: unknown) => (v === undefined || v === null || v === '' ? '' : typeof v === 'number' ? String(v) : String(v));

	const handleLatLngChange = (which: 'lat' | 'lng', value: string) => {
		setValue(`location.${which}`, value, { shouldDirty: true });
		const latStr = which === 'lat' ? value : latLngInputValue(watch('location.lat'));
		const lngStr = which === 'lng' ? value : latLngInputValue(watch('location.lng'));
		const latNum = parseFloat(latStr.replace(',', '.'));
		const lngNum = parseFloat(lngStr.replace(',', '.'));
		if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
			setValue('location.coords', { type: 'Point', coordinates: [lngNum, latNum] }, { shouldDirty: true });
		}
	};

	const getStreetAddressValue = (language: string) => {
		const street = getTranslationFieldValue(language, 'street');
		const streetNo = getTranslationFieldValue(language, 'street_no');
		const combined = [street, streetNo]
			.filter((value) => value && value.toString().trim().length > 0)
			.join(' ')
			.trim();

		if (combined.length > 0) {
			return combined;
		}

		const rootStreetAddress = watch('location.street_address');
		return (rootStreetAddress as string) ?? '';
	};

	const handlePinChange = async (dLat: number, dLng: number) => {
		setValue('location.lat', dLat, { shouldDirty: true });
		setValue('location.lng', dLng, { shouldDirty: true });
		setValue('location.coords', { type: 'Point', coordinates: [dLng, dLat] }, { shouldDirty: true });

		if (!window.google?.maps?.Geocoder) {
			return;
		}

		const latLng = new window.google.maps.LatLng(dLat, dLng);
		const geocoder = new window.google.maps.Geocoder();

		for (const { code } of BUSINESS_LANGUAGES) {
			await new Promise<void>((resolve) => {
				geocoder.geocode({ location: latLng, language: code }, (results, status) => {
					if (status === 'OK' && results?.[0]?.address_components) {
						applyAddressComponentsToLocationForm(results[0].address_components, code);
					}
					resolve();
				});
			});
		}

		await trigger('location.street_address');
	};

	const handleAddressChange = () => {
		if (!window.google || !inputRef.current) return;

		const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
			componentRestrictions: { country: 'gr' },
			fields: ['address_components', 'geometry', 'place_id'],
			types: ['address'],
		});

		// Create Places service for fetching details in multiple languages
		const service = new window.google.maps.places.PlacesService(document.createElement('div'));

		autocomplete.addListener('place_changed', () => {
			const place = autocomplete.getPlace();
			if (!place.place_id) return;

			// Fetch details for selected, default, and English translations to keep data in sync
			const languagesToFetch = new Set([teamNotesLang, defaultLocale, 'en']);
			Array.from(languagesToFetch).reduce((promise, languageCode) => {
				if (!languageCode) {
					return promise;
				}
				return promise.then(() => fetchPlaceDetails(place.place_id, languageCode, service));
			}, Promise.resolve());
		});
	};

	const fetchPlaceDetails = (placeId, language, service) => {
		const request = {
			placeId: placeId,
			language: language,
			fields: ['address_components', 'geometry'],
		};

		return new Promise<void>((resolve) => {
			service.getDetails(request, async (place, status) => {
				if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place?.geometry) {
					resolve();
					return;
				}

				try {
					// Set coordinates (not part of address translations)
					const lat = place.geometry.location.lat();
					const lng = place.geometry.location.lng();
					const coords = [lng, lat];
					setValue('location.coords', { type: 'Point', coordinates: coords }, { shouldDirty: true });
					setValue('location.lat', lat, { shouldDirty: true });
					setValue('location.lng', lng, { shouldDirty: true });

					applyAddressComponentsToLocationForm((place.address_components ?? []) as google.maps.GeocoderAddressComponent[], language);

					await trigger('location.street_address');
				} catch (error) {
					console.error('Failed to fetch place details', error);
				} finally {
					resolve();
				}
			});
		});
	};

	useEffect(() => {
		handleAddressChange();
	}, []);

	useEffect(() => {
		if (inputRef.current) {
			handleAddressChange();
		}
	}, [teamNotesLang, defaultLocale, watch]);

	const streetAddressValue = watch('location.street_address');
	const isAddressFilled = typeof streetAddressValue === 'string' ? streetAddressValue.trim().length > 0 : !!streetAddressValue;

	return (
		<>
			<div className="row pb-5">
				<div className="col-12">
					<div className="mb-0 fs-2 fw-bold">
						<FormattedMessage id="STORE.MODAL.DETAILS" />
					</div>
				</div>
			</div>
			<div className="p-3 generic-border">
				<LanguageSelector language={teamNotesLang} onLanguageChange={handleLanguageChange} />
				<div className="row pt-5">
					<Controller
						name="businessId"
						control={control}
						defaultValue={activeBusiness._id ?? ''}
						render={({ field }) => (
							<div className="col-12 col-sm-6">
								<div className="pb-3 fs-7 fw-bold">
									<FormattedMessage id="STORE.MODAL.DETAILS.ORGANIZATION" />
								</div>
								<div className="select-container">
									<select
										className="fs-6 fw-bold text-capitalize"
										aria-label="Select example"
										value={field.value}
										onChange={field.onChange}
									>
										{businesses &&
											businesses.map((business: { title: string; _id: string }, index: number) => (
												<option value={business?._id} key={index} className="">
													{business?.title}
												</option>
											))}
									</select>
									<i className="fa-solid fa-chevron-down arrow-icon"></i>
								</div>
							</div>
						)}
					/>

					<div className="col-12 col-sm-6">
						<div className="input-group">
							<label htmlFor="location-business-title" className="form-label pb-3 pt-5 pt-sm-0 mb-0 fs-7 fw-bold required">
								<FormattedMessage id="STORE.MODAL.DETAILS.TITLE" />
							</label>

							<input
								type="text"
								className={clsx('form-control fs-6 p-4', {
									'is-invalid': errors?.title && translations[teamNotesLang]?.title === '',
								})}
								id="location-business-title"
								aria-labelledby="location-business-title"
								placeholder={intl.formatMessage({ id: 'STORE.MODAL.DETAILS.TITLE' })}
								{...register(`translations.${teamNotesLang}.title`)}
								value={watch(`translations.${teamNotesLang}.title`) ?? ''}
							/>
							{errors.translations?.[defaultLocale]?.title ? (
								<div className="fv-plugins-message-container">
									<div className="fv-help-block">
										<span role="alert">{errors.translations[defaultLocale]?.title.message}</span>
									</div>
								</div>
							) : null}
						</div>
					</div>
				</div>
				<div className="row pt-5">
					<div className="col-12">
						<label htmlFor="location-description" className="form-label pb-3 mb-0 fs-7 fw-bold required">
							<FormattedMessage id="STORE.MODAL.DETAILS.DESCRIPTION" />
						</label>
						<textarea
							id="location-description"
							aria-labelledby="location-description"
							rows={4}
							cols={50}
							{...register(`translations.${teamNotesLang}.description`)}
							value={watch(`translations.${teamNotesLang}.description`) ?? ''}
							className={clsx('full-width-textarea fs-6 p-4', {
								'is-invalid': errors?.description && translations[teamNotesLang]?.description === '',
							})}
						/>

						{errors.translations?.[defaultLocale]?.description ? (
							<div className="fv-plugins-message-container">
								<div className="fv-help-block">
									<span role="alert">{errors.translations[defaultLocale]?.description.message}</span>
								</div>
							</div>
						) : null}
					</div>
				</div>
				<div className="row pt-5">
					<div className="col-12">
						<div className="pb-3 mb-0 fs-7 fw-bold">
							<FormattedMessage id="STORE.MODAL.DETAILS.MAP" defaultMessage="Location on map" />
						</div>
						<StoreLocationMap watch={watch} onPinChange={handlePinChange} />
					</div>
				</div>
				<div className="row pt-5">
					<div className="col-12">
						<div className="input-group">
							<label htmlFor="location-business-address" className="form-label pb-3 mb-0 fs-7 fw-bold required">
								<FormattedMessage id="STORE.MODAL.DETAILS.ADDRESS" />
							</label>
							<Controller
								name="location.street_address"
								control={control}
								render={({ field }) => (
									<input
										className={clsx('form-control fs-6 p-4', {
											'is-invalid': errors?.location?.street_address,
										})}
										value={field.value ?? getStreetAddressValue(teamNotesLang)}
										id={`location-business-address-${teamNotesLang}`}
										aria-labelledby={`location-business-address-${teamNotesLang}`}
										placeholder={intl.formatMessage({ id: 'STORE.MODAL.DETAILS.ADDRESS' })}
										onChange={(event) => field.onChange(event.target.value)}
										onBlur={field.onBlur}
										ref={(node) => {
											// @ts-ignore
											inputRef.current = node;
											field.ref(node);
										}}
									/>
								)}
							/>

							{errors?.location?.street_address ? (
								<div className="fv-plugins-message-container">
									<div className="fv-help-block">
										<span role="alert">{errors.location?.street_address.message}</span>
									</div>
								</div>
							) : null}
						</div>
					</div>
				</div>
				{isAddressFilled ? (
					<>
						<div className="row pt-5 g-5 g-sm-5">
							<div className="col-12 col-md-4">
								<label htmlFor={`location-street-${teamNotesLang}`} className="form-label pb-3 mb-0 fs-7 fw-bold">
									<FormattedMessage id="STORE.MODAL.DETAILS.STREET" defaultMessage="Street" />
								</label>
								<input
									id={`location-street-${teamNotesLang}`}
									name={`location.translations.${teamNotesLang}.street`}
									type="text"
									className="form-control form-control-solid fs-6 p-4"
									autoComplete="off"
									value={getTranslationFieldValue(teamNotesLang, 'street')}
									onChange={(event) => handleTranslationFieldChange(teamNotesLang, 'street', event.target.value)}
									placeholder={intl.formatMessage({ id: 'STORE.MODAL.DETAILS.STREET', defaultMessage: 'Street' })}
								/>
							</div>
							<div className="col-12 col-md-4">
								<label htmlFor={`location-street-no-${teamNotesLang}`} className="form-label pb-3 mb-0 fs-7 fw-bold">
									<FormattedMessage id="STORE.MODAL.DETAILS.STREET_NUMBER" defaultMessage="Number" />
								</label>
								<input
									id={`location-street-no-${teamNotesLang}`}
									name={`location.translations.${teamNotesLang}.street_no`}
									type="text"
									className="form-control form-control-solid fs-6 p-4"
									autoComplete="off"
									value={getTranslationFieldValue(teamNotesLang, 'street_no')}
									onChange={(event) => handleTranslationFieldChange(teamNotesLang, 'street_no', event.target.value)}
									placeholder={intl.formatMessage({
										id: 'STORE.MODAL.DETAILS.STREET_NUMBER',
										defaultMessage: 'Number',
									})}
								/>
							</div>
							<div className="col-12 col-md-4">
								<label htmlFor="location-post-code" className="form-label pb-3 mb-0 fs-7 fw-bold">
									<FormattedMessage id="STORE.MODAL.DETAILS.POSTAL_CODE" defaultMessage="Postal Code" />
								</label>
								<input
									id="location-post-code"
									name="location.post_code"
									type="text"
									className="form-control form-control-solid fs-6 p-4"
									autoComplete="off"
									value={watch('location.post_code') ?? ''}
									onChange={(event) => {
										const value = event.target.value;
										setValue('location.post_code', value, { shouldDirty: true });
									}}
									placeholder={intl.formatMessage({
										id: 'STORE.MODAL.DETAILS.POSTAL_CODE',
										defaultMessage: 'Postal Code',
									})}
								/>
							</div>
						</div>
						<div className="row pt-5 g-5 g-sm-5">
							<div className="col-12 col-md-4">
								<label htmlFor={`location-city-${teamNotesLang}`} className="form-label pb-3 mb-0 fs-7 fw-bold">
									<FormattedMessage id="STORE.MODAL.DETAILS.CITY" defaultMessage="City" />
								</label>
								<input
									id={`location-city-${teamNotesLang}`}
									name={`location.translations.${teamNotesLang}.city`}
									type="text"
									className="form-control form-control-solid fs-6 p-4"
									autoComplete="off"
									value={getTranslationFieldValue(teamNotesLang, 'city')}
									onChange={(event) => handleTranslationFieldChange(teamNotesLang, 'city', event.target.value)}
									placeholder={intl.formatMessage({ id: 'STORE.MODAL.DETAILS.CITY', defaultMessage: 'City' })}
								/>
							</div>
							<div className="col-12 col-md-4">
								<label htmlFor={`location-area-${teamNotesLang}`} className="form-label pb-3 mb-0 fs-7 fw-bold">
									<FormattedMessage id="STORE.MODAL.DETAILS.AREA" defaultMessage="Area" />
								</label>
								<input
									id={`location-area-${teamNotesLang}`}
									name={`location.translations.${teamNotesLang}.area`}
									type="text"
									className="form-control form-control-solid fs-6 p-4"
									autoComplete="off"
									value={getTranslationFieldValue(teamNotesLang, 'area')}
									onChange={(event) => handleTranslationFieldChange(teamNotesLang, 'area', event.target.value)}
									placeholder={intl.formatMessage({ id: 'STORE.MODAL.DETAILS.AREA', defaultMessage: 'Area' })}
								/>
							</div>
							<div className="col-12 col-md-4">
								<label htmlFor={`location-country-${teamNotesLang}`} className="form-label pb-3 mb-0 fs-7 fw-bold">
									<FormattedMessage id="STORE.MODAL.DETAILS.COUNTRY" defaultMessage="Country" />
								</label>
								<select
									id={`location-country-${teamNotesLang}`}
									name={`location.translations.${teamNotesLang}.country`}
									className="form-select form-select-solid fs-6 p-4"
									value={getTranslationFieldValue(teamNotesLang, 'country') ?? ''}
									onChange={(event) => handleTranslationFieldChange(teamNotesLang, 'country', event.target.value)}
									aria-label={intl.formatMessage({
										id: 'STORE.MODAL.DETAILS.COUNTRY',
										defaultMessage: 'Country',
									})}
									disabled={isLoadingCountries}
								>
									<option value="">
										{intl.formatMessage({
											id: 'STORE.MODAL.DETAILS.COUNTRY',
											defaultMessage: 'Country',
										})}
									</option>
									{countries.map((country) => (
										<option key={country.value} value={country.value}>
											{country.value}
										</option>
									))}
								</select>
							</div>
							<div className="col-12 col-md-4">
								<label htmlFor="location-latitude" className="form-label pb-3 mb-0 fs-7 fw-bold">
									<FormattedMessage id="STORE.MODAL.DETAILS.LATITUDE" defaultMessage="Latitude" />
								</label>
								<input
									id="location-latitude"
									name="location.lat"
									type="text"
									inputMode="decimal"
									autoComplete="off"
									className="form-control form-control-solid fs-6 p-4"
									value={latLngInputValue(watch('location.lat'))}
									onChange={(event) => handleLatLngChange('lat', event.target.value)}
									placeholder={intl.formatMessage({
										id: 'STORE.MODAL.DETAILS.LATITUDE',
										defaultMessage: 'Latitude',
									})}
								/>
							</div>
							<div className="col-12 col-md-4">
								<label htmlFor="location-longitude" className="form-label pb-3 mb-0 fs-7 fw-bold">
									<FormattedMessage id="STORE.MODAL.DETAILS.LONGITUDE" defaultMessage="Longitude" />
								</label>
								<input
									id="location-longitude"
									name="location.lng"
									type="text"
									inputMode="decimal"
									autoComplete="off"
									className="form-control form-control-solid fs-6 p-4"
									value={latLngInputValue(watch('location.lng'))}
									onChange={(event) => handleLatLngChange('lng', event.target.value)}
									placeholder={intl.formatMessage({
										id: 'STORE.MODAL.DETAILS.LONGITUDE',
										defaultMessage: 'Longitude',
									})}
								/>
							</div>
						</div>
					</>
				) : null}
			</div>
		</>
	);
};

export default StoreDetails;
